<template>
    <div class="flex flex-col gap-2">
        <div v-if="model" class="flex gap-2">
            <div class="flex flex-col gap-2">
                <div class="flex gap-2">
                    <div class="text-lg">{{ $t('orgs.create-organization') }}</div>
                    <UButton
                        icon="i-ph-arrow-clockwise"
                        variant="ghost"
                        @click="handleClickRegenerate"
                    />
                </div>
                <UFormField label="Org Nummer">
                    <UInput
                        v-model="model.orgNo"
                        label="Org Nummer"
                        class="w-1/4 min-w-[300px]"
                        :placeholder="$t('orgs.org-nr')"
                        data-cy="create-org-input-orgno"
                    />
                </UFormField>
                <UFormField label="Navn">
                    <UInput
                        v-model="model.name"
                        label="Navn"
                        class="w-1/4 min-w-[300px]"
                        :placeholder="$t('orgs.name')"
                        data-cy="create-org-input-orgname"
                    />
                </UFormField>
                <UButton
                    v-if="model"
                    class="w-fit"
                    data-cy="create-new-org-button"
                    :disabled="!orgNumLengthIsValid(model.orgNo) || isLoading"
                    @click="handleSubmit"
                >
                    {{ $t('orgs.create-organization-button') }}
                    <progress v-if="isLoading" class="progress w-24 absolute"/>
                </UButton>
            </div>
            <div class="flex flex-col gap-2">
                <div class="self-center">Generelt</div>
                <USwitch v-model="model.bankSettings.enabled" label="Sett opp bank"/>
                <USwitch v-model="model.ehfSettings.enabled" label="Sett opp EHF"/>
                <USwitch v-model="model.vatSettings.enabled" label="Sett opp MVA"/>
                <USwitch v-model="model.seedSettings.enabled" label="Fyll opp med data"/>
            </div>
            <div v-if="model.bankSettings.enabled" class="flex flex-col gap-2">
                <div class="self-center">Bank Innstillinger</div>
                <div class="flex gap-2">
                    <div class="flex flex-col gap-2">
                        <UFormField label="Konto Navn">
                            <UInput v-model="model.bankSettings.accountName" label="Konto Navn"/>
                        </UFormField>
                        <UFormField label="Konto Nummer">
                            <UInput v-model="model.bankSettings.accountNo" label="Konto Nummer"/>
                        </UFormField>
                    </div>
                    <div class="flex flex-col gap-2">
                        <UFormField label="Regnskapskonto">
                            <UInput v-model="model.bankSettings.glAccountNo" label="Regnskapskonto"/>
                        </UFormField>
                        <USwitch v-model="model.bankSettings.isIncome" label="Standard inntektskonto"/>
                        <USwitch v-model="model.bankSettings.isPayment" label="Standard betalingskonto"/>
                    </div>
                </div>
            </div>
            <div v-if="model.seedSettings.enabled" class="flex flex-col gap-2">
                <div class="self-center">Seed Instillinger</div>
                <div class="flex gap-2 max-h-36">
                    <div class="flex flex-col gap-2 flex-wrap">
                        <UFormField label="# Produkt">
                            <UInput v-model="model.seedSettings.productCount"/>
                        </UFormField>
                        <UFormField label="# Kunder">
                            <UInput v-model="model.seedSettings.customerCount"/>
                        </UFormField>
                        <UFormField label="# Leverandører">
                            <UInput v-model="model.seedSettings.supplierCount"/>
                        </UFormField>
                        <UFormField label="# Bilag">
                            <UTooltip :title="isTransactionSeedable ? undefined : 'Må ha kunder og leverandører.'">
                                <UInput
                                    v-model="model.seedSettings.transactionCount"
                                    :disabled="!isTransactionSeedable"
                                    :leading-icon="!isTransactionSeedable ? 'i-ph-warning' : undefined"
                                />
                            </UTooltip>
                        </UFormField>
                        <UFormField label="# Invoices (todo)">
                            <UTooltip :title="isInvoiceSeedable ? undefined : 'Må ha produkt, bank og kunde.'">
                                <UInput
                                    v-model="model.seedSettings.invoiceCount"
                                    :disabled="!isInvoiceSeedable"
                                    :leading-icon="!isInvoiceSeedable ? 'i-ph-warning' : undefined"
                                />
                            </UTooltip>
                        </UFormField>
                        <UFormField label="# EHF invoices (todo)">
                            <UTooltip :title="isEhfSeedable ? undefined : 'Må ha EHF, produkt, bank og kunde.'">
                                <UInput
                                    v-model="model.seedSettings.ehfCount"
                                    :disabled="!isEhfSeedable"
                                    :leading-icon="!isEhfSeedable ? 'i-ph-warning' : undefined"
                                />
                            </UTooltip>
                        </UFormField>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {orgNumLengthIsValid} from '@/tenants/org.service'
import {getRandomCompanyName} from '@/services/random.service'
import {getRandomOrgNo} from '../../cypress/support/helper'
import {provisionPatronOrg} from '@/services/infrastructure/patron.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import {post} from '@/services/infrastructure/api.service'

const isLoading = ref(false)
const model = ref<TenantSetupRequest>()

type TenantSetupRequest = {
    orgNo: string,
    name: string,
    bankSettings: {
        enabled: boolean,
        accountNo: string,
        accountName: string,
        glAccountNo: string | null,
        isIncome: boolean,
        isPayment: boolean,
    },
    ehfSettings: {
        enabled: boolean
    },
    vatSettings: {
        enabled: boolean,
    },
    seedSettings: {
        enabled: boolean,
        productCount: number,
        customerCount: number,
        supplierCount: number,
        transactionCount: number,
        invoiceCount: number,
        ehfCount: number
    }
}

const isTransactionSeedable = computed(() => {
    if (!model.value) {
        return false
    }
    if (model.value?.seedSettings.customerCount < 1) {
        return false
    }
    return model.value.seedSettings.supplierCount >= 1
})

const isInvoiceSeedable = computed(() => {
    if (!model.value) {
        return false
    }
    if (model.value.seedSettings.customerCount < 1) {
        return false
    }
    if (!model.value.bankSettings.enabled || !model.value.bankSettings.glAccountNo || !model.value.bankSettings.accountNo) {
        return false
    }
    return model.value.seedSettings.productCount >= 1

})

const isEhfSeedable = computed(() => {
    if (!model.value) {
        return false
    }
    if (!model.value.ehfSettings.enabled) {
        return false
    }
    if (model.value.seedSettings.customerCount < 1) {
        return false
    }
    if (!model.value.bankSettings.enabled || !model.value.bankSettings.glAccountNo || !model.value.bankSettings.accountNo) {
        return false
    }
    return model.value.seedSettings.productCount >= 1

})

const handleClickRegenerate = async () => {
    try {
        isLoading.value = true
        model.value = await getDummyOrg()
    } catch (error) {
        notifyError({title: 'Ops!', description: {descriptionMessage: 'Klarte ikke å regenerere', error}})
    } finally {
        isLoading.value = false
    }
}

const handleSubmit = async () => {
    if (!model.value) {
        return
    }
    try {
        isLoading.value = true
        const tenantResponse = await provisionPatronOrg(model.value.orgNo, model.value.name)
        const payload: TenantSetupRequest = {
            ...model.value,
        }
        if (!isTransactionSeedable.value) {
            payload.seedSettings.transactionCount = 0
        }
        if (!isInvoiceSeedable.value) {
            payload.seedSettings.invoiceCount = 0
        }
        if (!isEhfSeedable.value) {
            payload.seedSettings.ehfCount = 0
        }
        await setupTenant(model.value, tenantResponse.organiztionId)
        await handleClickRegenerate()
    } catch (error) {
        notifyError({title: 'Ops!', description: {descriptionMessage: 'Klarte ikke å lagre', error}})
    } finally {
        isLoading.value = false
    }
}

const getDummyOrg = async (): Promise<TenantSetupRequest> => {
    const name = await getRandomCompanyName()
    return {
        orgNo: getRandomOrgNo(),
        name,
        bankSettings: {
            enabled: true,
            accountName: 'Bankkonto',
            accountNo: '00000000000',
            glAccountNo: '1920',
            isIncome: true,
            isPayment: true,
        },
        ehfSettings: {
            enabled: true,
        },
        vatSettings: {
            enabled: false
        },
        seedSettings: {
            enabled: true,
            productCount: 10,
            customerCount: 10,
            supplierCount: 10,
            transactionCount: 10,
            invoiceCount: 10,
            ehfCount: 10,
        }
    }
}

const setupTenant = async (request: TenantSetupRequest, tenantId: String) => {
    await post('dummy-generator/setup', request, {
        tenantId
    })
}

onMounted(async () => {
    await handleClickRegenerate()
})

</script>

<style scoped>

</style>